import { Song, SongModel } from "../Entities/SongModel";
import { useCallback, useEffect, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "";

/* =========================
   API Client
========================= */

export interface FetchSongsParams {
  search?: string;
  difficulty?: string;
  tag?: string;
  mine?: boolean;
  skip?: number;
  start?: number;
  limit?: number;
  page?: number;
}

export interface FetchSongsResponse {
  items: SongModel[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export interface ArtistSummary {
  name: string;
  song_count: number;
  tags?: string[];
  image_url?: string;
}

export interface ArtistDetail {
  name: string;
  song_count: number;
  songs: SongModel[];
  tags: string[];
  difficulties: string[];
  image_url?: string;
}

export async function apiFetchArtists(search?: string): Promise<ArtistSummary[]> {
  const query = new URLSearchParams();
  if (search && search.trim()) query.set("search", search.trim());
  const res = await fetch(`${API_BASE}/api/artists?${query.toString()}`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`Failed to fetch artists: ${res.statusText}`);
  }
  return await res.json();
}

export async function apiFetchArtist(name: string): Promise<ArtistDetail | null> {
  const res = await fetch(`${API_BASE}/api/artists/${encodeURIComponent(name.trim())}`, { credentials: "include" });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Failed to fetch artist ${name}: ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    songs: (data.songs || []).map((s: any) => new SongModel(s)),
  };
}


export async function apiFetchSongs(params: FetchSongsParams = {}): Promise<FetchSongsResponse> {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.difficulty && params.difficulty !== "all") query.set("difficulty", params.difficulty);
  if (params.tag && params.tag !== "all") query.set("tag", params.tag);
  if (params.mine) query.set("mine", "true");
  if (params.skip !== undefined) query.set("skip", params.skip.toString());
  if (params.start !== undefined) query.set("start", params.start.toString());
  if (params.limit !== undefined) query.set("limit", params.limit.toString());
  if (params.page !== undefined) query.set("page", params.page.toString());

  const res = await fetch(`${API_BASE}/api/songs?${query.toString()}`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`Failed to fetch songs: ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    items: (data.items || []).map((s: any) => new SongModel(s)),
  };
}

export async function apiFetchUserSongs(search?: string): Promise<SongModel[]> {
  const res = await apiFetchSongs({ mine: true, search, limit: 100 });
  return res.items;
}

export async function apiFetchRandomSongs(limit: number = 50): Promise<SongModel[]> {
  const res = await fetch(`${API_BASE}/api/songs/random?limit=${limit}`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`Failed to fetch random songs: ${res.statusText}`);
  }
  const data = await res.json();
  return (data || []).map((s: any) => new SongModel(s));
}

export async function apiFetchSong(id: number): Promise<SongModel | null> {
  const res = await fetch(`${API_BASE}/api/songs/${id}`, { credentials: "include" });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Failed to fetch song ${id}: ${res.statusText}`);
  }
  const data = await res.json();
  return new SongModel(data);
}

export async function apiGetSongByUrl(url: string): Promise<SongModel | null> {
  const clean = url.trim();
  if (!clean) return null;
    const res = await fetch(`${API_BASE}/api/songs/by-url?url=${encodeURIComponent(clean)}`, { credentials: "include" });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Could not look up song (${res.status}). Please retry.`);
    const data = await res.json();
    return new SongModel(data);
}

function getCsrfHeader(): Record<string, string> {
  const match = typeof document !== "undefined" ? document.cookie.match(/(^|;\s*)chordmaster_csrf=([^;]*)/) : null;
  const token = match ? decodeURIComponent(match[2]) : null;
  return token ? { "X-CSRF-Token": token } : {};
}

export async function apiUpsertSong(song: Partial<Song>): Promise<SongModel> {
  const res = await fetch(`${API_BASE}/api/songs`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getCsrfHeader() },
    credentials: "include",
    body: JSON.stringify(song),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to save song: ${res.statusText}`);
  }
  const data = await res.json();
  return new SongModel(data);
}

export async function apiCreateSong(song: Partial<Song>): Promise<SongModel> {
  return apiUpsertSong(song);
}

export async function apiUpdateSong(id: number, song: Partial<Song>): Promise<SongModel> {
  const res = await fetch(`${API_BASE}/api/songs/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getCsrfHeader() },
    credentials: "include",
    body: JSON.stringify(song),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to update song: ${res.statusText}`);
  }
  const data = await res.json();
  return new SongModel(data);
}

export async function apiDeleteSong(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/songs/${id}`, {
    method: "DELETE",
    headers: { ...getCsrfHeader() },
    credentials: "include",
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete song: ${res.statusText}`);
  }
}

export interface ExternalSearchResult {
  title: string;
  artist: string;
  url: string;
  source: string;
  type?: string;
  rating?: number;
  votes?: number;
  version?: number;
  existing_id?: number;
}

export interface BatchScrapeResult {
  imported: SongModel[];
  failed: string[];
  total_requested: number;
  total_imported: number;
}

export async function apiSearchExternalSongs(query: string): Promise<ExternalSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/scrape/search?query=${encodeURIComponent(trimmed)}`, { credentials: "include" });
  } catch (err: any) {
    throw new Error(`Network error connecting to API: ${err.message || err}`);
  }

  if (!res.ok) {
    let errorDetail = `Search failed (Status: ${res.status})`;
    try {
      const errData = await res.json();
      if (errData && errData.detail) {
        errorDetail =
          typeof errData.detail === "string"
            ? errData.detail
            : JSON.stringify(errData.detail);
      }
    } catch {
      if (res.statusText) {
        errorDetail = `Search failed: ${res.statusText}`;
      }
    }
    throw new Error(errorDetail);
  }

  return await res.json();
}

export async function apiScrapeUrl(url: string): Promise<SongModel> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/scrape`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ url: url.trim() }),
    });
  } catch (err: any) {
    throw new Error(`Network error connecting to API: ${err.message || err}`);
  }

  if (!res.ok) {
    let errorDetail = `Failed to scrape song (Status: ${res.status})`;
    try {
      const errData = await res.json();
      if (errData && errData.detail) {
        errorDetail =
          typeof errData.detail === "string"
            ? errData.detail
            : JSON.stringify(errData.detail);
      }
    } catch {
      if (res.statusText) {
        errorDetail = `Failed to scrape song: ${res.statusText}`;
      }
    }
    throw new Error(errorDetail);
  }

  const data = await res.json();
  const scrapedSong = new SongModel(data);
  if (scrapedSong.id) {
    await idbUpsertSong(scrapedSong);
  }
  return scrapedSong;
}

export async function apiBatchScrapeUrls(urls: string[]): Promise<BatchScrapeResult> {
  const res = await fetch(`${API_BASE}/api/scrape/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ urls }),
  });
  if (!res.ok) {
    throw new Error(`Batch scrape failed: ${res.statusText}`);
  }
  const data = await res.json();
  const importedModels = (data.imported || []).map((s: any) => new SongModel(s));
  for (const s of importedModels) {
    if (s.id) await idbUpsertSong(s);
  }
  return {
    ...data,
    imported: importedModels,
  };
}


/* =========================
   IndexedDB helper
========================= */

const DB_NAME = "songs-db";
const STORE = "songs";
const VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/* =========================
   Storage Layer
========================= */

async function idbGetSong(id: number): Promise<SongModel | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const req = store.get(id);

      req.onsuccess = () => {
        if (!req.result) return resolve(null);
        resolve(new SongModel(req.result));
      };

      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbUpsertSong(song: Song): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);

      store.put({
        ...song,
        created_at: typeof song.created_at === "object" ? song.created_at?.toISOString() : song.created_at,
        song_date: typeof song.song_date === "object" ? song.song_date?.toISOString() : song.song_date,
      });

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // fallback or ignore IDB error
  }
}

/* =========================
   Hook
========================= */

export function useSongsModel() {
  const cache = useRef<Map<number, SongModel>>(new Map());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  const getSong = useCallback(async (id: number): Promise<SongModel | null> => {
    try {
      const apiSong = await apiFetchSong(id);
      if (!apiSong) return null;
      cache.current.set(id, apiSong);
      await idbUpsertSong(apiSong);
      return apiSong;
    } catch (err) {
      console.warn("API fetch error, loading cached song", err);
    }

    if (cache.current.has(id)) return cache.current.get(id)!;
    const cachedSong = await idbGetSong(id);
    if (cachedSong && cachedSong.body && cachedSong.body.length > 0) {
      cache.current.set(id, cachedSong);
      return cachedSong;
    }

    const raw = localStorage.getItem("song_" + id);
    if (!raw) return null;

    try {
      const parsed = new SongModel(JSON.parse(raw));
      cache.current.set(id, parsed);
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const upsertSong = useCallback(async (song: Partial<Song>): Promise<SongModel> => {
    let savedSong: SongModel;
    try {
      savedSong = await apiUpsertSong(song);
    } catch (e) {
      console.warn("Could not upsert to API, saving locally:", e);
      savedSong = new SongModel(song);
    }

    if (savedSong.id) {
      cache.current.set(savedSong.id, savedSong);
      await idbUpsertSong(savedSong);
      localStorage.setItem(
        "song_" + savedSong.id,
        JSON.stringify(savedSong)
      );
    }

    return savedSong;
  }, []);

  return {
    ready,
    getSong,
    upsertSong,
    fetchSongs: apiFetchSongs,
    fetchRandomSongs: apiFetchRandomSongs,
    scrapeUrl: apiScrapeUrl,
  };
}
